/**
 * @fileoverview
 * Layout Component - Main application layout with sidebar and header
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import {
  Component,
  signal,
  computed,
  HostListener,
  ViewChild,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { SidebarComponent } from './sidebar/sidebar.component';
import { HeaderComponent } from './header/header.component';
import { LoaderComponent } from '../loader/loader.component';
import {
  LoaderService,
  NotificationWebSocketService,
  NotificationApiService,
  SelectedBranchService,
} from '@core/services';

import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, HeaderComponent, LoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.css',
})
export class LayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('headerRef') headerRef!: HeaderComponent;
  @ViewChild('sidebarRef') sidebarRef!: SidebarComponent;

  private readonly loaderService = inject(LoaderService);
  private readonly selectedBranchService = inject(SelectedBranchService);
  private readonly notificationWs = inject(NotificationWebSocketService);
  private readonly notificationApi = inject(NotificationApiService);

  sidebarCollapsed = signal(false);
  isMobile = signal(false);
  mobileSidebarOpen = signal(false);
  private routerSubscription?: Subscription;

  /**
   * Global loader: feature loaders, or branch auto-select when header has no branch yet
   * (e.g. fresh login with forceNearest). Skips flash when a persisted branch is already shown.
   */
  private readonly awaitingBranchSelection = computed(
    () =>
      this.selectedBranchService.initializing() &&
      !this.selectedBranchService.selectedBranch()
  );

  readonly loading = computed(
    () => this.loaderService.loading() || this.awaitingBranchSelection()
  );
  readonly loaderMessage = computed(() =>
    this.awaitingBranchSelection()
      ? 'Selecting nearest branch...'
      : this.loaderService.message()
  );

  constructor(private readonly router: Router) {
    // Initialize mobile state - use more accurate detection
    this.isMobile.set(this.checkIsMobile());
    if (this.isMobile()) {
      this.sidebarCollapsed.set(false);
    }
  }

  private checkIsMobile(): boolean {
    // Treat any screen width < 1280px as mobile/tablet (including tablets)
    // This makes tablets behave exactly like mobile devices
    return window.innerWidth < 1280;
  }

  ngOnInit() {
    this.notificationWs.connect();
    this.notificationApi.loadHistory().subscribe();
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.setTitleFromRoute());
  }

  ngAfterViewInit() {
    this.setTitleFromRoute();
  }

  ngOnDestroy() {
    this.notificationWs.disconnect();
    this.routerSubscription?.unsubscribe();
  }

  /** Read title from route data (deepest route with data.title wins). */
  private getTitleFromRouteData(route: ActivatedRoute): string | null {
    let title: string | null = null;
    let current: ActivatedRoute | null = route;
    while (current) {
      const t = current.snapshot.data['title'];
      if (typeof t === 'string' && t) title = t;
      current = current.firstChild;
    }
    return title;
  }

  private getProfileTitle(url: string): string | null {
    if (url === '/profile/view-profile' || url.startsWith('/profile/view-profile')) {
      return 'Profile';
    }
    return null;
  }

  private findSubmenuTitle(menuItems: any[], url: string): string | null {
    for (const item of menuItems) {
      if (!item.hasSubmenu || !item.submenus) continue;
      let matched = item.submenus.find(
        (sub: any) =>
          sub.route != null &&
          (url === sub.route || url.startsWith(sub.route + '/'))
      );
      if (!matched) {
        matched = this.matchLoanProcessSubmenu(item.submenus, url);
      }
      if (matched) return matched.label;
    }
    return null;
  }

  private matchLoanProcessSubmenu(submenus: any[], url: string): any {
    const loan = submenus.find((sub: any) => sub.label === 'Loan Process Checking');
    if (!loan?.route) return null;
    const segments = loan.route.split('/').filter((s: string) => s.length > 0);
    const parentRoute = '/' + segments.slice(0, -1).join('/');
    const underParent =
      url.startsWith(parentRoute + '/') &&
      url !== parentRoute &&
      url !== parentRoute + '/';
    return underParent ? loan : null;
  }

  private findRegularMenuTitle(menuItems: any[], url: string): string | null {
    const item = [...menuItems]
      .filter((i) => i.route != null && !i.hasSubmenu)
      .sort((a, b) => (b.route?.length ?? 0) - (a.route?.length ?? 0))
      .find(
        (i) =>
          i.route != null &&
          (url === i.route ||
            (i.route !== '/dashboard' && url.startsWith(i.route + '/')))
      );
    return item?.label ?? null;
  }

  private setTitleFromRoute() {
    if (!this.headerRef || !this.sidebarRef) {
      setTimeout(() => this.setTitleFromRoute(), 0);
      return;
    }

    const url = this.router.url.split('?')[0];
    const routeTitle = this.getTitleFromRouteData(this.router.routerState.root);
    if (routeTitle) {
      this.headerRef.updateTitle(routeTitle);
      return;
    }

    const profileTitle = this.getProfileTitle(url);
    if (profileTitle) {
      this.headerRef.updateTitle(profileTitle);
      return;
    }

    const menuItems = this.sidebarRef.menuItems();
    const submenuTitle = this.findSubmenuTitle(menuItems, url);
    if (submenuTitle) {
      this.headerRef.updateTitle(submenuTitle);
      return;
    }

    const regularTitle = this.findRegularMenuTitle(menuItems, url);
    this.headerRef.updateTitle(regularTitle ?? 'Dashboard');
  }

  toggleSidebar() {
    if (this.isMobile()) {
      const willOpen = !this.mobileSidebarOpen();
      this.mobileSidebarOpen.set(willOpen);
      // Ensure sidebar is uncollapsed when opening on mobile/tablet
      if (willOpen) {
        this.sidebarCollapsed.set(false);
      }
    } else {
      this.sidebarCollapsed.update((collapsed) => !collapsed);
    }
  }

  onSidebarToggle(collapsed: boolean) {
    this.sidebarCollapsed.set(collapsed);
  }

  onMobileSidebarClose() {
    this.mobileSidebarOpen.set(false);
  }

  onActiveMenuTitleChange(title: string) {
    if (this.headerRef) {
      this.headerRef.updateTitle(title);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    const wasMobile = this.isMobile();
    this.isMobile.set(this.checkIsMobile());

    // Only apply mobile behavior if actually detected as mobile
    if (this.isMobile()) {
      if (!wasMobile) {
        // Just switched to mobile - ensure sidebar is ready
        this.sidebarCollapsed.set(false);
      }
      // Close sidebar if it was open and we're resizing
      if (window.innerWidth < 768) {
        this.mobileSidebarOpen.set(false);
      }
    } else {
      // Switched to desktop - close mobile sidebar
      this.mobileSidebarOpen.set(false);
    }
  }
}
